import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Cpu, LineChart as LineChartIcon, RefreshCw, Sparkles, Square } from 'lucide-react';

import { TrainingService } from '@/services/TrainingService';
import { TrainingPackageService } from '@/services/TrainingPackageService';
import { TERMINAL_STATUSES, type TrainingJob } from '@/types/training';
import { selfServeTiers, type TrainingPackageCredit } from '@/lib/marketing';
import JobStatusBadge from './JobStatusBadge';
import StartTrainingDialog from './StartTrainingDialog';
import JobDetailDialog from './JobDetailDialog';
import MetricsDrawer from './MetricsDrawer';
import { formatStage } from './stageLabels';

type Props = {
  userId: string | null;
  corpusReady: boolean;
  defaultName: string;
  isAdmin: boolean;
};

const ACTIVE_POLL_MS = 4000;
const IDLE_POLL_MS = 30000;

export default function TrainingPanel({ userId, corpusReady, defaultName, isAdmin }: Props) {

  const { toast } = useToast();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<TrainingJob[]>([]);
  const [credits, setCredits] = useState<TrainingPackageCredit[]>([]);
  const [loading, setLoading] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [openMetricsJobId, setOpenMetricsJobId] = useState<string | null>(null);
  const [stoppingId, setStoppingId] = useState<string | null>(null);

  const availableCredits = useMemo(() => credits.filter((credit) => credit.status === 'available'), [credits]);

  const activeCredits = useMemo(() => credits.filter((credit) => credit.status === 'active'), [credits]);
  const activeJobs = useMemo(() => jobs.filter((job) => !TERMINAL_STATUSES.includes(job.status)), [jobs]);
  const metricsJob = useMemo(() => jobs.find((job) => job.id === openMetricsJobId) ?? null, [jobs, openMetricsJobId]);

  const refreshCredits = useCallback(async () => {

    if (!userId) return;
    setCreditsLoading(true);
    try {
      const list = await TrainingPackageService.listCredits();
      setCredits(list);
    } catch (e: any) {
      toast({
        title: 'Could not load training credits',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setCreditsLoading(false);
    }
  }, [userId, toast]);


  const refreshJobs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const list = await TrainingService.listJobs({ limit: 20 });
      setJobs(list.jobs);
    } catch (e: any) {
      toast({
        title: 'Could not load training jobs',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshJobs(), refreshCredits()]);
  }, [refreshCredits, refreshJobs]);

  const stopJob = useCallback(
    async (jobId: string) => {
      const confirmed =
        typeof window !== 'undefined'
          ? window.confirm('Stop this training and release the GPU node? Any partial progress will be lost.')
          : true;
      if (!confirmed) return;
      setStoppingId(jobId);
      try {
        await TrainingService.cancelJob(jobId);
        toast({
          title: 'Training stopped',
          description: 'The GPU node will release shortly.',
        });
        await refreshAll();
      } catch (e: any) {
        toast({
          title: 'Could not stop training',
          description: String(e?.message || e),
          variant: 'destructive',
        });
      } finally {
        setStoppingId(null);
      }
    },
    [toast, refreshAll],
  );

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!userId) return;
    const hasActive = jobs.some((j) => !TERMINAL_STATUSES.includes(j.status));
    const interval = window.setInterval(() => void refreshAll(), hasActive ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    return () => window.clearInterval(interval);
  }, [jobs, refreshAll, userId]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-canada-red" />
            Training
          </CardTitle>
          <CardDescription>Run sovereign fine-tuning jobs on CanXP GPU nodes.</CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={() => void refreshAll()} disabled={loading || creditsLoading} aria-label="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading || creditsLoading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Package credits</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Each self-serve package is a one-time training credit. When you launch a run, the credit becomes active
                and expires automatically when the training finishes.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/purchase#self-serve')}>
              Buy training package
            </Button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {credits.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/70 p-4 text-sm text-muted-foreground sm:col-span-2">
                No self-serve training credits yet. Buy a package to unlock a one-time training session.
              </div>
            ) : (
              credits.map((credit) => {
                const tier = selfServeTiers.find((entry) => entry.id === credit.package_id);
                return (
                  <div key={credit.id} className="rounded-lg border border-border/70 bg-background/70 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{tier?.name || credit.package_id}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Purchased {new Date(credit.created_at).toLocaleString()}</p>
                      </div>
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${
                          credit.status === 'available'
                            ? 'bg-green-500/10 text-green-600'
                            : credit.status === 'active'
                              ? 'bg-primary/10 text-primary'
                              : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {credit.status}
                      </span>
                    </div>
                    {credit.training_job_id ? (
                      <p className="mt-3 text-xs text-muted-foreground">Linked to training job {credit.training_job_id.slice(0, 8)}…</p>
                    ) : (
                      <p className="mt-3 text-xs text-muted-foreground">Unused credit — ready to launch.</p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <Button className="w-full" onClick={() => setShowStart(true)} disabled={!corpusReady || !userId || (!isAdmin && availableCredits.length === 0)}>

          <Sparkles className="mr-2 h-4 w-4" />
          {corpusReady
            ? availableCredits.length > 0
              ? 'Start training'
              : isAdmin
                ? 'Start training (admin override)'
                : activeCredits.length > 0
                  ? 'Training credit already in use'
                  : 'Buy a package to enable training'
            : 'Upload corpus to enable'}

        </Button>

        <div className="space-y-2">
          {activeJobs.length === 0 ? (
            <p className="rounded-md border bg-muted/20 p-4 text-sm text-muted-foreground">
              No active training jobs right now. Your completed trained models appear in the section below.
            </p>
          ) : (
            activeJobs.map((j) => (
              <div
                key={j.id}
                className="relative w-full rounded-md border border-l-2 border-l-canada-red bg-canada-red/[0.02] transition hover:bg-muted/40"
              >
                <button type="button" onClick={() => setOpenJobId(j.id)} className="w-full p-3 text-left">
                  <div className="flex items-center justify-between gap-2 pr-28 sm:pr-32">

                    <p className="truncate font-medium">{j.output_name}</p>
                    <JobStatusBadge status={j.status} />
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">
                      {j.base_model.split('/').pop()} · {j.training_type.toUpperCase()} · {Math.round((j.progress || 0) * 100)}%
                    </span>
                    <span>{new Date(j.created_at).toLocaleString()}</span>
                  </div>
                  {j.stage ? <p className="mt-1 truncate text-xs italic text-muted-foreground">{formatStage(j.stage)}</p> : null}
                </button>
                <div className="absolute right-2 top-2 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMetricsJobId(j.id);
                    }}
                    title="Open training charts"
                    className="inline-flex items-center justify-center rounded-md border border-border/70 bg-background/90 px-2.5 py-1.5 text-xs font-medium text-foreground shadow-sm transition hover:bg-muted"
                    aria-label="Open training charts"
                  >
                    <LineChartIcon className="mr-1.5 h-3.5 w-3.5" />
                    Charts
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void stopJob(j.id);
                    }}
                    disabled={stoppingId === j.id}
                    title="Stop training and release the GPU node"
                    className="inline-flex items-center justify-center rounded-md p-1.5 text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Stop training"
                  >
                    <Square className="h-4 w-4 fill-current" />
                  </button>
                </div>

              </div>
            ))
          )}
        </div>

      </CardContent>

      <StartTrainingDialog
        open={showStart}
        onOpenChange={setShowStart}
        userId={userId}
        defaultName={defaultName}
        isAdmin={isAdmin}
        availableCredits={availableCredits}
        onJobCreated={(id) => {

          setOpenJobId(id);
          void refreshAll();
        }}
      />

      <JobDetailDialog
        open={!!openJobId}
        onOpenChange={(o) => {
          if (!o) {
            setOpenJobId(null);
            void refreshAll();
          }
        }}
        jobId={openJobId}
      />

      <MetricsDrawer
        open={!!openMetricsJobId}
        onOpenChange={(open) => {
          if (!open) setOpenMetricsJobId(null);
        }}
        job={metricsJob}
      />
    </Card>
  );
}
